import type { Game } from "@rally/rating";
import { useMutation } from "@tanstack/react-query";
import { useCallback, useState } from "react";
import { hasRole, useAuth } from "../lib/auth.js";
import { scoreFixture } from "../lib/draw.js";
import { confirmMatch, formatScoreline } from "../lib/matches.js";
import { FORMATS } from "../lib/sport.js";
import type { MatchFormat } from "../lib/types.js";
import { Button, Chips, Notice } from "../components/ui.js";
import { ScorePad } from "./ScorePad.js";

/**
 * Entering the score of a drawn match.
 *
 * The same pad as §5.2's log screen, and deliberately none of its setup: who is playing is
 * the draw's answer, not a thing to be picked again courtside. `score_fixture` reads the
 * sides off the fixture and ignores anything a client might have to say about them, so the
 * two cannot disagree.
 *
 * The format is still asked, because it decides when the match is clinched and a club plays
 * a shorter one at a tournament than on a Tuesday. It is a label and a stopping rule only —
 * the rating reads games won, never the target.
 */
export function ScoreFixture({
  fixtureId,
  sequence,
  round,
  nameA,
  nameB,
  onCancel,
  onDone,
}: {
  fixtureId: string;
  sequence: number;
  round: number;
  nameA: string;
  nameB: string;
  onCancel: () => void;
  onDone: () => void;
}) {
  const { member } = useAuth();
  const authoritative = hasRole(member, "admin");

  const [format, setFormat] = useState<MatchFormat>(FORMATS[0]!);
  const [games, setGames] = useState<Game[]>([]);
  const [reviewing, setReviewing] = useState(false);

  const submit = useMutation({
    mutationFn: async () => {
      const matchId = await scoreFixture(fixtureId, games);
      // As on the log screen: an admin's entry is authoritative, so it rates on the spot
      // rather than waiting for a tap from somebody who has gone home. The maths still
      // happens in the Edge Function, because there is one code path for it.
      if (authoritative) await confirmMatch(matchId);
      return matchId;
    },
    onSuccess: onDone,
  });

  const onGamesChange = useCallback((next: Game[]) => setGames(next), []);

  return (
    <div className="mb-6 border-t-2 pt-4" style={{ borderColor: "var(--color-line)" }}>
      <div className="mb-3 flex items-baseline justify-between gap-3">
        <h3 className="micro">
          Match #{sequence} · Round {round}
        </h3>
        <button
          type="button"
          onClick={onCancel}
          className="micro underline"
          style={{ minHeight: 44 }}
        >
          Cancel
        </button>
      </div>

      {reviewing ? (
        <>
          <div className="mb-1">{nameA}</div>
          <div className="text-ink-muted">vs {nameB}</div>
          <div className="font-mono mt-3 text-lg">{formatScoreline(games)}</div>
          <div className="micro mt-2">{winnerName(games, nameA, nameB)} won</div>

          <Notice tone="muted">
            {authoritative
              ? "You're an admin, so this counts straight away — no confirmation needed."
              : "Scores wait on someone from the other side to confirm before they change any rating."}
          </Notice>

          <div className="flex flex-wrap gap-3">
            <Button tone="primary" onClick={() => submit.mutate()} disabled={submit.isPending}>
              {submit.isPending ? "Saving…" : "Save the score"}
            </Button>
            <Button onClick={() => setReviewing(false)} disabled={submit.isPending}>
              Fix the score
            </Button>
          </div>

          {submit.error ? <Notice tone="attention">{submit.error.message}</Notice> : null}
        </>
      ) : (
        <>
          <div className="mb-4">
            <Chips
              options={FORMATS.map((f) => ({ value: formatKey(f), label: formatChip(f) }))}
              selected={[formatKey(format)]}
              onToggle={(value) => {
                const found = FORMATS.find((f) => formatKey(f) === value);
                if (found) setFormat(found);
              }}
            />
          </div>

          <ScorePad
            nameA={nameA}
            nameB={nameB}
            format={format}
            onGamesChange={onGamesChange}
            onDone={() => setReviewing(true)}
          />
        </>
      )}
    </div>
  );
}

function winnerName(games: Game[], nameA: string, nameB: string): string {
  let a = 0;
  let b = 0;
  for (const game of games) {
    if (game.a > game.b) a++;
    else b++;
  }
  return a > b ? nameA : nameB;
}

const formatKey = (f: MatchFormat) => `${f.bestOf}x${f.target}`;
const formatChip = (f: MatchFormat) =>
  f.bestOf === 1 ? `1 × ${f.target}` : `${f.bestOf} × ${f.target}`;
